"use strict";

import FileSaver from "file-saver";
import { isNumber } from "lodash";
import queryString from "query-string";

/**
 * 获取对象数据类型
 * @param obj
 * @returns {string}
 */
export const getEsType = obj =>
    Object.prototype.toString.call(obj).slice(8, -1);

/**
 * 生成UUID
 * @param len 生成随机字符串的长度，默认16位
 */
export const uuid = (len = 16) => {
    if (len > 16) {
        len = 16;
    }
    let d = new Date().getTime();
    return "xxxxxxxxxxxx-4xxx-yxxx-xxxxxxxxxxxx"
        .slice(0, len)
        .replace(/[xy]/g, function(c) {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
};

/**
 * 数字四舍五入
 * @param v 数字
 * @param p 精度
 */
export const round = (v, p) => {
    p = Math.pow(10, p >>> 31 ? 0 : p | 0);
    v *= p;
    return ((v + 0.5 + (v >> 31)) | 0) / p;
};

/**
 * 数字截取
 * @param {Number} n 数字
 * @param {Number} p 位数
 */
export const toFixed = (n, p) => {
    if (!isNumber(n)) {
        n = Number(n);
    }

    return parseFloat(n.toFixed(p));
};

/**
 * 导出文件
 * @param res 文件内容流
 * @param name 文件导出名
 * @param type 文件类型
 */
export const saveAsFile = (res, name, type = "excel") => {
    const getImageSuffix = res =>
        res.substring(res.lastIndexOf("."), res.length);
    const saveTypesOptions = [
        {
            type: "text",
            contentType: "text/plain;charset=utf-8",
            suffix: ".txt"
        },
        {
            type: "excel",
            contentType: "application/vnd.ms-excel",
            suffix: ".xlsx"
        },
        {
            type: "csv",
            contentType: "application/vnd.ms-excel",
            suffix: ".csv"
        },
        {
            type: "image",
            contentType: `image/${getImageSuffix(res).slice(1)}`,
            suffix: getImageSuffix(res)
        }
    ];
    const getTypeOption = type =>
        saveTypesOptions.filter(item => item.type === type);
    const blob = new Blob([res], {
        type: getTypeOption(type).contentType
    });

    FileSaver.saveAs(blob, `${name}${getTypeOption(type).suffix}`);
};

/**
 * 异步延迟执行
 * @param t 时间毫秒数
 * @param v 执行函数
 * @returns {Promise<any>}
 */
export const delay = (t, v) => {
    return new Promise(function(resolve) {
        setTimeout(resolve.bind(null, v), t);
    });
};

/**
 * 符合JSON规范数据的深拷贝，日期格式要求为 `xxxx-xx-xx xx:xx:xx`
 * @param obj 数据源
 * @returns {any}
 */
export const clone = obj => {
    const regExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}\d{2}\.\d{3}Z$/;
    return JSON.parse(JSON.stringify(obj), function(k, v) {
        if (typeof v === "string" && regExp.test(v)) {
            return new Date(v);
        }
        return v;
    });
};

/**
 * 计算相对时间
 * @param {number} stamp 时间缀
 */
export const timeAgo = stamp => {
    const minute = 1000 * 60;
    const hour = minute * 60;
    const day = hour * 24;
    const week = day * 7;
    const month = day * 30;
    const now = new Date().getTime();
    const difftime = now - stamp;

    if (difftime < 0) return;
    const minuteDiff = difftime / minute;
    const hourDiff = difftime / hour;
    const dayDiff = difftime / day;
    const weekDiff = difftime / week;
    const monthDiff = difftime / month;

    if (monthDiff >= 1 && monthDiff < 3) {
        return `${parseInt(monthDiff)}月前`;
    } else if (weekDiff >= 1 && weekDiff < 4) {
        return `${parseInt(weekDiff)}周前`;
    } else if (dayDiff >= 1 && dayDiff < 6) {
        return `${parseInt(dayDiff)}天前`;
    } else if (hourDiff >= 1 && hourDiff < 24) {
        return `${parseInt(hourDiff)}小时前`;
    } else if (minuteDiff >= 1 && minuteDiff < 59) {
        return `${parseInt(minuteDiff)}分钟前`;
    } else if (difftime >= 0 && difftime <= minute) {
        return "刚刚";
    }
};

/**
 * 根据条件（是否在大屏）是否打开新的搜索选项卡
 */
export const openBlankSearchPage = (router, location) => {
    if (sessionStorage.getItem("powersign") === "1") {
        router.push(location);
    } else {
        const href = `${window.location.origin}/${
            location.path
        }?${queryString.stringify(location.query)}`;
        window.open(href, "_blank");
    }
    // const href = `${window.location.origin}/${location.path}?${queryString.stringify(location.query)}`
    // window.open(href, '_blank')
};

/**
 * 数据来源跳转
 * @param {String} href 文章来源地址
 */
export const openSourceLink = href => {
    if (sessionStorage.getItem("powersign") === "1") {
        window.open(href, "_self");
    } else {
        window.open(href, "_blank");
    }
};

/** 在大屏幕中 */
export const useWithScreen = () => {
    return sessionStorage.getItem("powersign") === "1";
};

/**
 * 对象是否为空
 */
export const isEmpty = obj => {
    return Object.keys(obj).length === 0;
};

/**
 * 数组展开成一维
 * @param {Array} a 数据
 */
export const flatten = a => {
    return Array.isArray(a) ? [].concat(...a.map(flatten)) : a;
};

/**
 * 判断图片URL
 * @param {String} url 图片地址
 */
export const isImageUrl = url => {
    const reg = /(?:(?:https?:\/\/))[-a-zA-Z0-9@:%.\+~#=]{2,256}\.[a-z]{2,4}\b(?:[-a-zA-Z0-9@:%_\+.~#?&\/=]*(\.jpg|\.png|\.jpeg))/gm;
    return reg.test(url);
};

/**
 * 获取浏览器参数
 * @param {String} url 浏览器地址
 */
export const parseUrl = url => {
    const praStr = url.split("?")[1];
    if (!praStr) {
        return {};
    }
    const praArr = praStr.split("&");
    const res = {};
    for (let i = 0; i < praArr.length; i += 1) {
        const arr = praArr[i].split("=");
        const [key, value] = arr;
        res[key] = value;
    }

    return res;
};

/**
 * 获取浏览器cookie
 * @param {String} name cookie名
 */
export const getCookie = name => {
    var strcookie = document.cookie; //获取cookie字符串
    var arrcookie = strcookie.split("; "); //分割
    //遍历匹配
    for (var i = 0; i < arrcookie.length; i++) {
        var arr = arrcookie[i].split("=");
        if (arr[0] == name) {
            return arr[1];
        }
    }
    return "";
};

export default {
    getEsType,
    uuid,
    round,
    toFixed,
    saveAsFile,
    delay,
    clone,
    timeAgo,
    openBlankSearchPage,
    openSourceLink,
    useWithScreen,
    flatten,
    isImageUrl,
    parseUrl,
    getCookie
};
